<template>
  <section class="image-cards-widget grid">
    <div class="container">
      <div
        v-for="({ title, description, image, link }, index) in widget.cards"
        :key="`${title}-${index}`"
        class="card"
      >
        <div class="content">
          <div class="header">
            <TextClampedTooltip>
              <p
                ref="titles"
                class="title line-clamp-2 t-h4"
                v-html="nbsp(title)"
              />

              <template #content>
                <p class="t-p-s-130-bold" v-html="nbsp(title)" />
                <p class="t-p-s-130 t-color-2" v-html="nbsp(description)" />
              </template>
            </TextClampedTooltip>
            <BaseButton
              color="tertiary"
              :icon="ArrowRightLIcon"
              :base-link="omitLinkLabel(link)"
            />
          </div>
          <TextClampedTooltip>
            <p
              class="description line-clamp-2 t-p-m-130"
              v-html="nbsp(description)"
            />

            <template #content>
              <p class="t-p-s-130-bold" v-html="nbsp(title)" />
              <p class="t-p-s-130 t-color-2" v-html="nbsp(description)" />
            </template>
          </TextClampedTooltip>
        </div>
        <div class="card-image-wrapper">
          <BaseImage class="card-image" :image="image" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import ArrowRightLIcon from '@/assets/icons/arrow-right-l.svg?raw'
import type {
  PublicLink,
  PublicImageCardsWidget
} from '@/service/__generated-api'
import type { Widget } from '@/types/utils'

const props = defineProps<Widget<PublicImageCardsWidget>>()

const omitLinkLabel = (link: PublicLink): PublicLink => ({ ...link, label: '' })

const titles = useElementsSameHeight({
  watchSource: () => props.widget.cards
})
</script>

<style scoped lang="scss">
.image-cards-widget {
  @include widget-gradient-background;
}

.container {
  @include media-column(
    (
      xs: 12
    )
  );
  @include flex-col;
  gap: rem(24px);

  @include media-up(s-lg) {
    flex-direction: row;
    gap: rem(20px);
  }

  @include media-up(lg) {
    gap: rem(32px);
  }
}

.card {
  display: flex;
  flex-direction: column-reverse;
  gap: rem(24px);
  justify-content: flex-end;
  background-color: $layout-background-02;
  border-radius: rem(24px);

  @include card-hover-shadow {
    border-radius: rem(24px);
  }

  @include media-up(s-lg) {
    flex: 1 0 0;
  }
}

.card-image-wrapper {
  padding: rem(14px) rem(14px) 0;
}

.card-image {
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: rem(12px);

  @include media-up(md) {
    aspect-ratio: 3 / 2;
  }

  @include media-up(s-lg) {
    height: rem(272px);
    aspect-ratio: unset;
  }

  :deep(img) {
    @include card-hover-image-scale('.card:hover &');
  }
}

.content {
  padding: 0 rem(14px) rem(30px);

  @include media-up(md) {
    padding-inline: rem(28px);
  }

  @include media-up(s-lg) {
    min-height: rem(120px);
    padding: 0 rem(14px) rem(24px);
  }
}

.header {
  display: flex;
  gap: rem(20px);
  justify-content: space-between;
  padding-block-end: rem(4px);
}

.title {
  margin-block: rem(2px) rem(10px);
}

.description {
  min-height: rem(42px);
  color: $layout-text-02;
}
</style>
